import React, { useState } from 'react';
import { formatJson } from '../../../../utils/formatJson';

interface StepAttempt {
  attempt_number: number;
  status: 'completed' | 'failed';
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  error?: string;
  response?: any;
}

interface StepExecution {
  step_index: number;
  step_name: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  input: any;
  output: any;
  error?: string;
  attempts?: StepAttempt[];
  poll_count?: number;
  retry_count?: number;
}

interface TracingLocalization {
  stepLabel?: string;
  status?: {
    pending?: string;
    running?: string;
    completed?: string;
    failed?: string;
    waiting_callback?: string;
  };
  input?: string;
  output?: string;
  response?: string;
  error?: string;
  duration?: string;
  attempts?: string;
  attemptNumber?: string;
  pollCount?: string;
  retryCount?: string;
  noSteps?: string;
  skipped?: string;
  resendStreaming?: string;
  resendBlocking?: string;
  resending?: string;
  resendResult?: string;
  httpStatus?: string;
}

interface ResendResult {
  loading?: boolean;
  response?: any;
  httpStatus?: number;
  error?: string;
  duration_ms?: number;
  responseMode?: string;
}

interface TracingTabProps {
  stepHistory: StepExecution[];
  localization?: TracingLocalization;
  onResendStep?: (stepIndex: number, responseMode: 'streaming' | 'blocking') => void;
  resendResults?: Record<number, ResendResult>;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  completed: { bg: 'bg-green-900/30', text: 'text-green-400', dot: 'bg-green-400' },
  failed: { bg: 'bg-red-900/30', text: 'text-red-400', dot: 'bg-red-400' },
  pending: { bg: 'bg-gray-800/50', text: 'text-gray-400', dot: 'bg-gray-500' },
  running: { bg: 'bg-blue-900/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  waiting_callback: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  skipped: { bg: 'bg-gray-800/50', text: 'text-gray-500', dot: 'bg-gray-600' },
};

function getStatusStyle(status: string) {
  return STATUS_STYLES[status] || STATUS_STYLES.pending;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}



const TracingTab: React.FC<TracingTabProps> = ({ stepHistory, localization: loc, onResendStep, resendResults }) => {
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  if (!stepHistory || stepHistory.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8" data-testid="tracing-empty">
        {loc?.noSteps || 'No steps to display'}
      </div>
    );
  }

  const toggleStep = (index: number) => {
    setExpandedSteps(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusLabel = (status: string): string => {
    if (loc?.status) {
      const statusMap = loc.status as Record<string, string | undefined>;
      if (statusMap[status]) return statusMap[status]!;
    }
    return status;
  };

  return (
    <div className="space-y-2" data-testid="tracing-timeline">
      {stepHistory.map((step, index) => {
        const style = getStatusStyle(step.status);
        const isExpanded = expandedSteps[index] ?? false;
        const hasAttempts = step.attempts && step.attempts.length > 1;

        return (
          <div
            key={index}
            className="border border-gray-700 rounded-lg overflow-hidden"
            data-testid={`tracing-step-${index}`}
          >
            {/* Step header */}
            <button
              onClick={() => toggleStep(index)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800/50 hover:bg-gray-800 transition-colors text-left"
              data-testid={`tracing-step-header-${index}`}
            >
              {/* Status dot */}
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.dot}`} />

              {/* Step number & name */}
              <span className="text-xs font-mono text-gray-500 flex-shrink-0">
                #{index + 1}
              </span>
              <span className="text-sm font-medium text-white truncate flex-1">
                {step.step_name || `${loc?.stepLabel || 'Step'} ${index + 1}`}
              </span>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {(step.poll_count != null && step.poll_count > 0) && (
                  <span
                    className="px-1.5 py-0.5 text-xs bg-blue-900/40 text-blue-300 rounded font-mono"
                    data-testid={`tracing-step-${index}-poll-count`}
                  >
                    {loc?.pollCount || 'Poll'}: {step.poll_count}
                  </span>
                )}
                {(step.retry_count != null && step.retry_count > 0) && (
                  <span
                    className="px-1.5 py-0.5 text-xs bg-orange-900/40 text-orange-300 rounded font-mono"
                    data-testid={`tracing-step-${index}-retry-count`}
                  >
                    {loc?.retryCount || 'Retries'}: {step.retry_count}
                  </span>
                )}
                <span className={`px-2 py-0.5 text-xs rounded ${style.bg} ${style.text}`}>
                  {getStatusLabel(step.status)}
                </span>
                <span className="text-xs text-gray-500 font-mono w-16 text-right">
                  {formatDuration(step.duration_ms)}
                </span>
                <span className="text-gray-500 text-sm">
                  {isExpanded ? '▾' : '▸'}
                </span>
              </div>
            </button>

            {/* Step details (expanded) */}
            {isExpanded && (
              <div className="border-t border-gray-700 px-4 py-3 space-y-3" data-testid={`tracing-step-details-${index}`}>
                {/* Input section */}
                {step.input != null && (
                  <div>
                    <button
                      onClick={() => toggleSection(`${index}-input`)}
                      className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-300 transition-colors mb-1"
                      data-testid={`tracing-step-${index}-input-toggle`}
                    >
                      <span>{expandedSections[`${index}-input`] ? '▾' : '▸'}</span>
                      {loc?.input || 'Input'}
                    </button>
                    {expandedSections[`${index}-input`] && (
                      <pre className="bg-gray-900 border border-gray-700 rounded p-3 text-xs text-gray-300 overflow-auto max-h-48 whitespace-pre-wrap" data-testid={`tracing-step-${index}-input-content`}>
                        {formatJson(step.input)}
                      </pre>
                    )}
                  </div>
                )}

                {/* Output section */}
                {step.output != null && (
                  <div>
                    <button
                      onClick={() => toggleSection(`${index}-output`)}
                      className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-300 transition-colors mb-1"
                      data-testid={`tracing-step-${index}-output-toggle`}
                    >
                      <span>{expandedSections[`${index}-output`] ? '▾' : '▸'}</span>
                      {loc?.output || 'Output'}
                    </button>
                    {expandedSections[`${index}-output`] && (
                      <pre className="bg-gray-900 border border-gray-700 rounded p-3 text-xs text-gray-300 overflow-auto max-h-48 whitespace-pre-wrap" data-testid={`tracing-step-${index}-output-content`}>
                        {formatJson(step.output)}
                      </pre>
                    )}
                  </div>
                )}

                {/* Error section */}
                {step.error && (
                  <div>
                    <div className="text-xs font-medium text-red-400 mb-1">
                      {loc?.error || 'Error'}
                    </div>
                    <pre className="bg-red-950/30 border border-red-900/50 rounded p-3 text-xs text-red-300 overflow-auto max-h-32 whitespace-pre-wrap" data-testid={`tracing-step-${index}-error`}>
                      {step.error}
                    </pre>
                  </div>
                )}

                {/* Attempts section */}
                {hasAttempts && (
                  <div>
                    <button
                      onClick={() => toggleSection(`${index}-attempts`)}
                      className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-300 transition-colors mb-1"
                      data-testid={`tracing-step-${index}-attempts-toggle`}
                    >
                      <span>{expandedSections[`${index}-attempts`] ? '▾' : '▸'}</span>
                      {loc?.attempts || 'Attempts'} ({step.attempts!.length})
                    </button>
                    {expandedSections[`${index}-attempts`] && (
                      <div className="space-y-1.5 pl-3 border-l-2 border-gray-700" data-testid={`tracing-step-${index}-attempts-list`}>
                        {step.attempts!.map((attempt, aIdx) => {
                          const aStyle = getStatusStyle(attempt.status);
                          return (
                            <div
                              key={aIdx}
                              className="flex items-center gap-2 text-xs"
                              data-testid={`tracing-step-${index}-attempt-${aIdx}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${aStyle.dot}`} />
                              <span className="text-gray-400">
                                {loc?.attemptNumber || 'Attempt'} {attempt.attempt_number}
                              </span>
                              <span className={`${aStyle.text}`}>
                                {attempt.status}
                              </span>
                              <span className="text-gray-500 font-mono">
                                {formatDuration(attempt.duration_ms)}
                              </span>
                              {attempt.error && (
                                <span className="text-red-400 truncate max-w-[200px]" title={attempt.error}>
                                  {attempt.error}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Resend controls */}
                {onResendStep && (
                  <div className="border-t border-gray-700 pt-3" data-testid={`tracing-step-${index}-resend`}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onResendStep(index, 'streaming')}
                        disabled={resendResults?.[index]?.loading}
                        className="px-3 py-1.5 text-xs bg-blue-900/40 hover:bg-blue-900/60 text-blue-300 rounded border border-blue-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`tracing-step-${index}-resend-streaming`}
                      >
                        {loc?.resendStreaming || 'Resend (streaming)'}
                      </button>
                      <button
                        onClick={() => onResendStep(index, 'blocking')}
                        disabled={resendResults?.[index]?.loading}
                        className="px-3 py-1.5 text-xs bg-orange-900/40 hover:bg-orange-900/60 text-orange-300 rounded border border-orange-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`tracing-step-${index}-resend-blocking`}
                      >
                        {loc?.resendBlocking || 'Resend (blocking)'}
                      </button>
                      {resendResults?.[index]?.loading && (
                        <span className="text-xs text-gray-400 animate-pulse" data-testid={`tracing-step-${index}-resend-loading`}>
                          {loc?.resending || 'Resending...'}
                        </span>
                      )}
                    </div>

                    {/* Resend result */}
                    {resendResults?.[index] && !resendResults[index].loading && (resendResults[index].response != null || resendResults[index].error) && (
                      <div className="mt-2 space-y-1" data-testid={`tracing-step-${index}-resend-result`}>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-gray-400">{loc?.resendResult || 'Resend Result'}</span>
                          {resendResults[index].httpStatus != null && (
                            <span className={`font-mono ${resendResults[index].httpStatus! >= 400 ? 'text-red-400' : 'text-green-400'}`} data-testid={`tracing-step-${index}-resend-http-status`}>
                              {loc?.httpStatus || 'HTTP'} {resendResults[index].httpStatus}
                            </span>
                          )}
                          {resendResults[index].duration_ms != null && (
                            <span className="text-gray-500 font-mono" data-testid={`tracing-step-${index}-resend-duration`}>
                              {formatDuration(resendResults[index].duration_ms)}
                            </span>
                          )}
                          {resendResults[index].responseMode && (
                            <span className="text-gray-500 text-xs">
                              ({resendResults[index].responseMode})
                            </span>
                          )}
                        </div>
                        {resendResults[index].error && (
                          <pre className="bg-red-950/30 border border-red-900/50 rounded p-2 text-xs text-red-300 overflow-auto max-h-32 whitespace-pre-wrap" data-testid={`tracing-step-${index}-resend-error`}>
                            {resendResults[index].error}
                          </pre>
                        )}
                        {resendResults[index].response != null && (
                          <pre className="bg-gray-900 border border-gray-700 rounded p-2 text-xs text-gray-300 overflow-auto max-h-48 whitespace-pre-wrap" data-testid={`tracing-step-${index}-resend-response`}>
                            {formatJson(resendResults[index].response)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TracingTab;
